import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getDramaById, getRelatedDramas, getCategoryName } from '@/mocks/dramas';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const dramaId = Number(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const drama = getDramaById(dramaId);

  if (!drama) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">剧集未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该剧集不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = getRelatedDramas(drama);
  const categoryName = getCategoryName(drama.category);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-4 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName, to: `/category/${drama.category}` },
                { label: drama.title },
              ]}
            />
          </div>
        </div>

        <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
          <img
            src={drama.bannerImage}
            alt={drama.title}
            title={`${drama.title} - ${drama.subtitle}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-50"></div>
        </section>

        <article className="px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full sm:w-[220px] md:w-[260px] mx-auto lg:mx-0">
                <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-200">
                  <img
                    src={drama.coverImage}
                    alt={drama.title}
                    title={`${drama.title} - ${drama.subtitle}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-1">
                  {drama.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{drama.subtitle}</p>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-600 text-xs font-medium rounded-full whitespace-nowrap">
                    <i className="ri-star-fill text-[10px]"></i>
                    {drama.rating} 分
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    {drama.year}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    {drama.region}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    共{drama.episodes}集
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {drama.status}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {drama.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-4">
                  <span className="text-sm text-foreground-500">导演：</span>
                  <strong className="text-sm text-foreground-800 font-medium">{drama.director}</strong>
                </div>
                <div className="mb-5">
                  <span className="text-sm text-foreground-500">主演：</span>
                  <strong className="text-sm text-foreground-800 font-medium">{drama.cast.join(' / ')}</strong>
                </div>

                <div className="mb-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {drama.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line text-lg"></i>
                    立即观看
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10 md:mt-14">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-5">剧集列表</h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 md:gap-3">
                {drama.episodeList.map((ep) => (
                  <button
                    key={ep.num}
                    className="px-3 py-2.5 bg-background-100 hover:bg-primary-50 hover:text-primary-600 text-foreground-700 rounded-lg text-xs font-medium transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    {ep.num}
                  </button>
                ))}
              </div>
            </div>

            {related.length > 0 && (
              <div className="mt-10 md:mt-14">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-900">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/article/${r.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[2/3] mb-3 bg-background-200">
                        <img
                          src={r.coverImage}
                          alt={r.title}
                          title={`${r.title} - ${r.subtitle}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            <i className="ri-star-fill text-[10px]"></i>
                            {r.rating}
                          </span>
                        </div>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {r.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${drama.title} - ${drama.subtitle}`,
          "description": drama.description,
          "author": { "@type": "Person", "name": drama.director },
          "datePublished": `${drama.year}-01-01`,
          "publisher": { "@type": "Organization", "name": "努努影院" }
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://nunumovie.com/" },
            { "@type": "ListItem", "position": 2, "name": categoryName, "item": `https://nunumovie.com/category/${drama.category}` },
            { "@type": "ListItem", "position": 3, "name": drama.title }
          ]
        })
      }} />
    </>
  );
}